import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CORE_FEATURE_KEY, CoreState } from '../reducers/pt-core.reducer';
import { PrimatooModule } from 'it/primatoo/openapi/api/org/schemas/common/PrimatooModule';
import { PtPermissions, UiAuthInfo } from '@pt/pt-models';

export const selectPtCoreModuleState =
  createFeatureSelector<CoreState>(CORE_FEATURE_KEY);
export const organization = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => state?.organization
);
export const organizationName = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => state?.organization?.name
);

export const organizationStorageInfo = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => state?.organizationStorageInfo
);
export const loginResponse = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => state?.loginResponse
);
export const activeModuleChatBot = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) =>
    state?.organization?.activeModules?.includes(PrimatooModule.CHATBOT)
);
export const activeModuleCybersec = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) =>
    state?.organization?.activeModules?.includes(PrimatooModule.CYBERSEC)
);

export const loginModules = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => state.loginModules || []
);
export const isActiveMicrosoftModule = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) =>
    !!(state.loginModules || []).find((m) => m === PrimatooModule.LOGIN_MS)
);

export const isSystemOrAdmin = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) =>
    state.loginResponse &&
    (state.loginResponse.system || state.loginResponse.permissions.admin)
);

export const isExternalUserManagement = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) =>
    !!(state?.organization?.activeModules || []).find(
      (m) => m === PrimatooModule.EXTERNAL_USER_MANAGEMENT
    )
);

export const authInfo = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => {
    if (state.loginResponse && state.organization) {
      const isAdmin = state.loginResponse.permissions?.admin ?? false;
      const isSystem = state.loginResponse.system ?? false;
      const isMicrosoftAuth = !!(state.organization.activeModules || []).find(
        (m) => m === PrimatooModule.LOGIN_MS
      );
      const isActiveExternalUserManagement = !!(
        state.organization.activeModules || []
      ).find((m) => m === PrimatooModule.EXTERNAL_USER_MANAGEMENT);
      const changePasswordSupported =
        !isSystem && !isMicrosoftAuth && !isActiveExternalUserManagement;
      const adminUserTwoFactorAuthentication =
        (isSystem || isAdmin) && !isMicrosoftAuth;
      const profileTwoFactorAuthentication =
        !isActiveExternalUserManagement && !isMicrosoftAuth;

      const changeUserPasswordSupported = isSystem && !isMicrosoftAuth;
      const authInfo: UiAuthInfo = {
        isAdmin,
        isSystem,
        isMicrosoftAuth,
        changePasswordSupported,
        profileTwoFactorAuthentication,
        adminUserTwoFactorAuthentication,
        changeUserPasswordSupported,
        isActiveExternalUserManagement,
      };
      return authInfo;
    }
    return undefined;
  }
);

export const isSystemOrAdminOrNoAuth = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => {
    const oauth = !!(state.loginModules || []).find(
      (m) => m === PrimatooModule.LOGIN_MS
    );
    const system = state.loginResponse.system ?? false;
    const admin = state.loginResponse.permissions.admin ?? false;

    return system || admin || !oauth;
  }
);

export const currentActivePermission = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => {
    const permission: PtPermissions[] = [];
    if (state && state?.loginResponse) {
      if (state.loginResponse.enumeratedPermissions) {
        permission.push(...state.loginResponse.enumeratedPermissions);
      }

      if (state.organization) {
        const activeModules = state.organization.activeModules || [];
        activeModules.forEach((m) => {
          switch (m) {
            case PrimatooModule.AI:
              permission.push(PtPermissions.ACTIVE_MODULE_AI);
              break;
            case PrimatooModule.COVID:
              permission.push(PtPermissions.ACTIVE_MODULE_COVID);
              break;
            case PrimatooModule.CYBERSEC:
              permission.push(PtPermissions.ACTIVE_MODULE_CYBERSEC);
              break;
            case PrimatooModule.DATABREACH:
              permission.push(PtPermissions.ACTIVE_MODULE_DATABREACH);
              break;
            case PrimatooModule.LIA:
              permission.push(PtPermissions.ACTIVE_MODULE_LIA);
              break;
          }
        });
      }
    }
    return permission.sort();
  }
);
export const currentLanguage = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => state?.loginResponse?.language as string
);
export const anyPermission = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => state?.anyPermission
);
export const legalEntity = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => state?.legalEntity
);
export const selectedLegalEntity = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => state?.selectedLegalEntity
);
export const organizationResellerLogo = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => state?.organization?.resellerLogo
);
export const organizationCss = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => state?.organization?.css
);
export const organizationCssUpdatedAt = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => state?.organization?.cssUpdatedAt
);
export const activeModules = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => state?.organization?.activeModules
);
export const activeModulesAndPermission = createSelector(
  activeModules,
  currentActivePermission,
  (activeModules, permission) => {
    return {
      activeModules: activeModules,
      activePermissions: permission,
    };
  }
);

export const loginAttempt = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => state?.loginAttempt
);
export const isOpenSessionInfo = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => state?.sessionNotifications.open
);
export const sessionNotifications = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => state?.sessionNotifications.data
);
export const routesNavigationAllowed = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => {
    const pendingSystemLogin = state?.pendingSystemLogin;
    if (pendingSystemLogin) {
      return false;
    }
    const loginResponse = state?.loginResponse;
    return loginResponse !== null && loginResponse !== undefined;
  }
);

export const currentUserId = createSelector(
  selectPtCoreModuleState,
  (state: CoreState) => {
    return state.loginResponse.id;
  }
);
